import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { useCustomer } from '../Customer/customerContext';
import { deactivateCustomerUser, deleteCustomerUserInvite } from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { LinkButton } from '../../../../components/button/ButtonLink';
import { EmptyState } from '../../../../components/empty-state/EmptyState';
import { formatDateTime } from '../../../../utils/date';
import { SimpleSelect, ISimpleSelectItem } from '../../../../components/select/SimpleSelect';
import { updateCustomerUserRole } from '../../../user/models';

const getRoleDisplayName = (role: string): string => {
  switch (role) {
    case 'CustomerAdmin':
      return 'Admin';
    case 'CustomerUser':
      return 'Gebruiker';
    default:
      return role;
  }
};

export const CustomerUsersPage = () => {
  const { customer, refreshData } = useCustomer();

  const roleOptions: ISimpleSelectItem[] = [
    { key: 'CustomerAdmin', name: 'Admin' },
    { key: 'CustomerUser', name: 'Gebruiker' },
  ];

  const handleRoleChange = async (userId: string, userName: string, newRole: string) => {
    try {
      await updateCustomerUserRole(userId, customer.id, newRole);
      toast.success(`Rol van ${userName} bijgewerkt naar ${getRoleDisplayName(newRole)}`);
      refreshData();
    } catch (err) {
      captureException(err);
      toast.error(getDisplayError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <LinkButton to="invite" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
          Nodig gebruiker uit
        </LinkButton>
      </div>

      <h2 className="heading-two mb-2">Uitgenodigde gebruikers</h2>

      {!customer.invitedUsers.length && <EmptyState title="Geen uitnodigingen gevonden" />}

      <div className="grid gap-4 grid-cols-2">
        {customer.invitedUsers.map((u) => {
          return (
            <div className="card p-4" key={u.id}>
              <div className="flex justify-between items-center">
                <div className="card-heading-text">{u.email}</div>

                <ConfirmDialog
                  triggerColor="danger"
                  triggerText={<TrashIcon className="button-icon" />}
                  title="Uitnodiging verwijderen?"
                  submitText="Verwijder uitnodiging"
                  description={
                    <div>
                      {`Ben je zeker dat je de uitnodiging voor ${u.email} wilt verwijderen van klant ${customer.name}?`}
                    </div>
                  }
                  onSubmit={async () => {
                    try {
                      await deleteCustomerUserInvite(customer.id, u.id);
                      refreshData();
                      toast.success('Uitnodiging verwijderd');
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon uitnodiging niet verwijderen: ${getDisplayError(err)}`);
                    }
                  }}
                />
              </div>

              <div className="card-subtle-text">{formatDateTime(u.createdAt)}</div>
            </div>
          );
        })}
      </div>

      <h2 className="heading-two mb-2 mt-8">Gebruikers</h2>

      {!customer.users.length && <EmptyState title="Geen gebruikers gevonden" />}

      <div className="grid grid-cols-1 gap-4">
        {customer.users
          .filter((v) => !!v.user.isActive)
          .map((u) => {
            return (
              <div className="card p-4 flex items-start justify-between gap-4" key={u.id}>
                <div className="flex-1">
                  <div className="card-heading-text">{u.user.name}</div>
                  <div className="card-text">{u.user.email}</div>
                  <div className="card-text mt-1">
                    <span className={u.user.isActive ? 'text-positive-text' : 'text-negative-text'}>
                      {u.user.isActive ? 'Actief' : 'Inactief'}
                    </span>
                  </div>
                </div>
                <div className="w-48">
                  <label className="block text-sm font-medium mb-1">Rol</label>
                  <SimpleSelect
                    items={roleOptions}
                    selectedItem={roleOptions.find((r) => r.key === u.role) ?? null}
                    onSelect={(item) => {
                      if (item && item.key !== u.role) {
                        handleRoleChange(u.user.id, u.user.name, item.key);
                      }
                    }}
                  />
                </div>
                <ConfirmDialog
                  triggerColor="danger"
                  triggerText={<TrashIcon className="button-icon" />}
                  title="Gebruiker verwijderen?"
                  submitText="Verwijder gebruiker"
                  description={
                    <div>
                      {`Ben je zeker dat je de gebruiker ${u.user.name} wilt verwijderen van klant ${customer.name}?`}
                    </div>
                  }
                  onSubmit={async () => {
                    try {
                      await deactivateCustomerUser(customer.id, u.user.id);
                      refreshData();
                      toast.success('Gebruiker verwijderd');
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon gebruiker niet verwijderen: ${getDisplayError(err)}`);
                    }
                  }}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};
